"use client";

import { useCallback, useEffect, useId, useState, type DragEvent } from "react";
import { Icon } from "@/lib/icons/Icon";
import styles from "./FileUpload.module.css";

/**
 * FileUpload — wspólna kontrolka wyboru plików dla formularzy React-CMS.
 *
 * **Dlaczego osobny komponent, a nie `<input type="file">` w `<Field>`:**
 * `Field` używa floating label opartego na `:placeholder-shown`, którego natywny
 * input plikowy nie wspiera - etykieta zostaje nad kontrolką i nachodzi na
 * przycisk „Wybierz pliki". Dlatego FileUpload renderuje WŁASNĄ etykietę i hint,
 * i celowo NIE jest opakowywany w `<Field>`.
 *
 * **Kontrolowany:** stan trzyma konsument (`value` / `onChange`), więc lista da się
 * czyścić po zapisie i walidować razem z resztą formularza. Natywny input jest
 * ukryty dostępnie (nie `display:none`) i resetowany po każdym wyborze - bez tego
 * ponowne wskazanie tego samego pliku nie wywołałoby `change`.
 *
 * **Dodawanie, nie podmienianie:** kolejny wybór (albo upuszczenie) DOKŁADA pliki
 * do listy. Klient wskazujący załączniki na raty nie traci poprzednich, a każdą
 * pozycję może usunąć osobno.
 *
 * Obsługuje drag & drop, klik w całą strefę oraz **wklejanie zrzutów ekranu ze
 * schowka** (Ctrl+V) - odpowiednik `IFORM::addPasteImage` z panelu administracyjnego.
 * Nasłuch jest na oknie, bo zrzut wkleja się odruchowo w dowolnym miejscu formularza;
 * przechwytywane są WYŁĄCZNIE obrazy, więc wklejanie tekstu do pól działa normalnie.
 *
 * @example
 * <FileUpload label="Załączniki" optional value={files} onChange={setFiles} multiple />
 */
export function FileUpload({
  label,
  hint,
  error,
  value,
  onChange,
  multiple = false,
  accept,
  disabled = false,
  required = false,
  optional = false,
  maxFiles,
  allowPaste = true,
  browseLabel = "Wybierz pliki",
  dropLabel = "Przeciągnij pliki tutaj lub",
  emptyRemoveLabel = "Usuń plik",
}: FileUploadProps) {
  const inputId = useId();
  const [dragging, setDragging] = useState(false);
  //Czy ostatnia proba przekroczyla limit - komunikat gasnie przy kolejnym udanym dodaniu.
  const [limitHit, setLimitHit] = useState(false);

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;
      if (!multiple) {
        onChange(incoming.slice(0, 1));
        return;
      }

      /* Odsiew duplikatow. Ten sam zrzut wklejony drugi raz (Ctrl+V dwa razy pod rzad,
         albo ta sama grafika ze schowka po przelaczeniu okna) to zawsze pomylka, a nie
         intencja - a przy wklejaniu nie widac tego tak jak przy wyborze z dysku, bo
         nazwa jest generowana i za kazdym razem inna. Porownujemy PO TRESCI: rozmiar,
         typ i czas modyfikacji. Nazwa nie nadaje sie na klucz wlasnie dlatego,
         ze dla wklejen jest sztuczna. */
      const signature = (f: File) => `${f.size}|${f.type}|${f.lastModified}`;
      const known = new Set(value.map(signature));

      const fresh = incoming.filter((f) => {
        const key = signature(f);
        if (known.has(key)) return false;
        //Dodajemy na biezaco - odsiewa tez duplikaty W OBREBIE jednej porcji.
        known.add(key);
        return true;
      });

      if (fresh.length === 0) return;

      /* Limit liczby plikow. Przycinamy do wolnego miejsca zamiast odrzucac cala
         porcje - gdy klient zaznaczy 15 plikow przy limicie 10, dostaje 10 i jasna
         informacje, a nie pusta liste bez wyjasnienia. Serwer i tak sprawdza limit
         ponownie; to jest wygoda, nie zabezpieczenie. */
      if (maxFiles !== undefined) {
        const room = Math.max(0, maxFiles - value.length);
        if (room === 0) {
          setLimitHit(true);
          return;
        }
        if (fresh.length > room) {
          setLimitHit(true);
          onChange([...value, ...fresh.slice(0, room)]);
          return;
        }
      }

      setLimitHit(false);
      onChange([...value, ...fresh]);
    },
    [multiple, onChange, value, maxFiles],
  );

  /* Wklejanie zrzutu ekranu. Schowek nie niesie nazwy pliku - nadajemy ja sami,
     bo bez rozszerzenia backend odrzucilby plik na walidacji typu. */
  useEffect(() => {
    if (!allowPaste || disabled) return;

    const onPaste = (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const images = items.filter((i) => i.kind === "file" && i.type.startsWith("image/"));
      if (images.length === 0) return;

      const files = images
        .map((item, index) => {
          const blob = item.getAsFile();
          if (!blob) return null;
          const ext = (blob.type.split("/")[1] || "png").replace(/[^a-z0-9]/gi, "");
          return new File([blob], `zrzut-ekranu-${Date.now()}-${index + 1}.${ext}`, {
            type: blob.type,
          });
        })
        .filter((f): f is File => f !== null);

      if (files.length === 0) return;

      //Dopiero teraz blokujemy zdarzenie - gdy w schowku nie bylo obrazu, paste ma dzialac normalnie.
      e.preventDefault();
      addFiles(files);
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [allowPaste, disabled, addFiles]);

  function handleDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    addFiles(Array.from(e.dataTransfer.files));
  }

  const zoneClass = [
    styles.dropzone,
    dragging ? styles.dragging : "",
    error ? styles.invalid : "",
    disabled ? styles.disabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.root}>
      {label && (
        <label className={styles.labelRow} htmlFor={inputId}>
          {label}
          {required && <span className={styles.required}>*</span>}
          {optional && !required && <span className={styles.optional}>(opcjonalne)</span>}
        </label>
      )}

      {/* Strefa jest etykietą powiązaną z ukrytym inputem - klik i klawiatura
          działają natywnie, bez przechwytywania zdarzeń. */}
      <label
        className={zoneClass}
        htmlFor={inputId}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <Icon name="files" className={styles.dropIcon} />
        <span className={styles.dropText}>{dropLabel}</span>
        <span className={styles.browse}>{browseLabel}</span>
        {allowPaste && !disabled && (
          <span className={styles.pasteHint}>lub wklej zrzut ekranu (Ctrl+V)</span>
        )}
      </label>

      <input
        id={inputId}
        type="file"
        className={styles.nativeInput}
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        aria-describedby={hint ? `${inputId}-hint` : undefined}
        aria-invalid={error ? true : undefined}
        onChange={(e) => {
          addFiles(Array.from(e.target.files ?? []));
          //Reset - inaczej wybór tego samego pliku drugi raz nie wywoła zdarzenia.
          e.target.value = "";
        }}
      />

      {value.length > 0 && (
        <ul className={styles.list}>
          {value.map((file, index) => (
            <li key={`${file.name}-${file.lastModified}-${index}`} className={styles.item}>
              <Icon name="file-text" className={styles.itemIcon} />
              <span className={styles.itemName}>{file.name}</span>
              <span className={styles.itemSize}>{formatSize(file.size)}</span>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                title={emptyRemoveLabel}
                aria-label={`${emptyRemoveLabel}: ${file.name}`}
                disabled={disabled}
              >
                <Icon name="x" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {limitHit && maxFiles !== undefined && (
        <p className={styles.error}>
          Możesz dołączyć maksymalnie {maxFiles} {maxFiles === 1 ? "plik" : "plików"}.
        </p>
      )}

      {error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        hint && (
          <p className={styles.hint} id={`${inputId}-hint`}>
            {hint}
          </p>
        )
      )}
    </div>
  );
}

/** Rozmiar pliku w czytelnej jednostce (kB do 1 MB, dalej MB). */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type FileUploadProps = {
  label?: string;
  /** Tekst pomocniczy pod kontrolką (ignorowany gdy `error`). */
  hint?: string;
  /** Komunikat błędu - aktywuje stan invalid. */
  error?: string | null;
  /** Wybrane pliki (kontrolowane przez konsumenta). */
  value: File[];
  onChange: (files: File[]) => void;
  multiple?: boolean;
  /** Filtr rozszerzeń/MIME dla okna wyboru (podpowiedź, NIE walidacja). */
  accept?: string;
  /** Gorny limit liczby plikow. Po jego osiagnieciu kontrolka nie przyjmuje kolejnych. */
  maxFiles?: number;
  /** Przechwytywanie obrazow ze schowka (Ctrl+V). Domyslnie wlaczone. */
  allowPaste?: boolean;
  disabled?: boolean;
  required?: boolean;
  optional?: boolean;
  browseLabel?: string;
  dropLabel?: string;
  emptyRemoveLabel?: string;
};
